// src/services/webpush.ts
// Service layer per Web Push (Fase 3).
//
// Tutti i path REST partono SENZA "/" iniziale perché api/axios.js
// imposta baseURL con trailing slash (vedi notes.ts).
import api from '../api/axios';

export interface BackendWebPushSubscription {
  id: number;
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent: string;
  created_at: string;
  last_seen_at: string;
}

// ----------------------------------------------------------------------------
// VAPID public key
// ----------------------------------------------------------------------------

/**
 * Restituisce la chiave VAPID pubblica.
 * Preferenza:
 *   1) env var build-time `REACT_APP_VAPID_PUBLIC_KEY` (zero round-trip)
 *   2) endpoint backend `/api/notifications/webpush/vapid_public_key/`
 */
export async function getVapidPublicKey(): Promise<string> {
  const fromEnv = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  const res = await api.get('api/notifications/webpush/vapid_public_key/');
  return res.data?.key || '';
}

// ----------------------------------------------------------------------------
// Subscription CRUD
// ----------------------------------------------------------------------------

export async function registerSubscription(
  sub: PushSubscription,
): Promise<BackendWebPushSubscription> {
  const json = sub.toJSON() as PushSubscriptionJSON;
  const res = await api.post('api/notifications/webpush/', {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: (navigator.userAgent || '').substring(0, 300),
  });
  return res.data;
}

export async function listSubscriptions(): Promise<BackendWebPushSubscription[]> {
  const res = await api.get('api/notifications/webpush/');
  // Listing è non-paginato (mixins.ListModelMixin senza pagination_class)
  return Array.isArray(res.data) ? res.data : res.data?.results || [];
}

export async function deleteSubscriptionById(id: number): Promise<void> {
  await api.delete(`api/notifications/webpush/${id}/`);
}

/** Cancella per endpoint (utile dal client che ha la PushSubscription nativa). */
export async function unregisterSubscription(endpoint: string): Promise<void> {
  const list = await listSubscriptions();
  const found = list.find((s) => s.endpoint === endpoint);
  if (found) await deleteSubscriptionById(found.id);
}

// ----------------------------------------------------------------------------
// Utility
// ----------------------------------------------------------------------------

/**
 * Converte una stringa VAPID base64url in `Uint8Array`.
 *
 * Critical: passare una `string` invece di `Uint8Array` ad
 * `applicationServerKey` produce `InvalidAccessError` su Chrome.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
